import {TruVuFrame} from './types';

/**
 * Renders a TruVu frame to canvas
 */

export async function renderToCanvas(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frame: any,
  canvas: HTMLCanvasElement
): Promise<void> {
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to get 2D context from canvas');
  }

  // Primary: Handle TruVu frames with JPEG blob URLs
  if (frame && frame.blobUrl && frame.width && frame.height) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        try {
          // Set canvas dimensions to match frame
          canvas.width = frame.width;
          canvas.height = frame.height;

          // Clear the canvas
          context.clearRect(0, 0, canvas.width, canvas.height);

          // Draw the JPEG image to canvas
          context.drawImage(img, 0, 0, frame.width, frame.height);
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(
          new Error(`Failed to load image from blob URL: ${frame.blobUrl}`)
        );
      };

      // Load the JPEG from blob URL
      img.src = frame.blobUrl;
    });
  }

  // Fallback: Handle TruVu format frames with raw RGB data
  if (frame && frame.rgbData && frame.width && frame.height) {
    // Set canvas dimensions to match frame
    canvas.width = frame.width;
    canvas.height = frame.height;

    // Clear the canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Convert RGB to RGBA format for canvas
    const rgbaData = new Uint8ClampedArray(frame.width * frame.height * 4);

    for (let i = 0; i < frame.rgbData.length; i += 3) {
      const rgbaIndex = (i / 3) * 4;
      rgbaData[rgbaIndex] = frame.rgbData[i]; // R
      rgbaData[rgbaIndex + 1] = frame.rgbData[i + 1]; // G
      rgbaData[rgbaIndex + 2] = frame.rgbData[i + 2]; // B
      rgbaData[rgbaIndex + 3] = 255; // A (fully opaque)
    }

    // Create ImageData and draw to canvas
    const imageData = new ImageData(rgbaData, frame.width, frame.height);
    context.putImageData(imageData, 0, 0);
    return;
  }

  // Handle legacy LibAV-style frames (VideoFrame, ImageBitmap, etc.)
  if (frame && (frame.codedWidth || frame.width)) {
    const width = frame.codedWidth || frame.width;
    const height = frame.codedHeight || frame.height;

    canvas.width = width;
    canvas.height = height;
    context.clearRect(0, 0, width, height);

    // Try to draw as ImageBitmap or VideoFrame
    try {
      context.drawImage(frame, 0, 0);
      return;
    } catch (error) {
      console.warn('Failed to draw frame as image:', error);
    }
  }

  console.warn('Unsupported frame format:', frame);
}

/**
 * Handles the existing frame structure from your tour player
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function printIndex(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  frames: any[],
  frameIndex: number,
  canvas: HTMLCanvasElement
): Promise<void> {
  if (
    !frames ||
    frames.length === 0 ||
    frameIndex < 0 ||
    frameIndex >= frames.length
  ) {
    console.warn('printIndex: Invalid frame index or empty frames array');
    return;
  }

  const frameWrapper = frames[frameIndex];
  if (!frameWrapper) {
    console.warn('printIndex: No frame at index', frameIndex);
    return;
  }

  // Your existing structure: { frameIndex, frame: actualFrame, clusterCount }
  const frame = frameWrapper.frame;
  if (!frame) {
    console.warn('printIndex: No frame data in wrapper at index', frameIndex);
    return;
  }

  await renderToCanvas(frame, canvas);
}

/**
 * Canvas renderer class for advanced usage
 */
export class TruVuCanvasRenderer {
  async renderFrame(
    frame: TruVuFrame,
    canvas: HTMLCanvasElement
  ): Promise<void> {
    await renderToCanvas(frame, canvas);
  }

  async renderFrameIndex(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    frames: any[],
    idx: number,
    canvas: HTMLCanvasElement
  ): Promise<void> {
    await printIndex(frames, idx, canvas);
  }

  async createImageBitmap(frame: TruVuFrame): Promise<ImageBitmap> {
    const canvas = document.createElement('canvas');
    await this.renderFrame(frame, canvas);
    return createImageBitmap(canvas);
  }
}

// Export a default instance for convenience
export const canvasRenderer = new TruVuCanvasRenderer();
